import React, { useCallback, useEffect, useRef, useState } from 'react'
import LinkEditorModal from './LinkEditorModal'
import {
  parseLinksFromHtml as parseLinksFromHtmlUtil,
  renderTextWithLinks as renderTextWithLinksUtil,
  adjustLinksOnTextChange,
} from './utils/linkUtils'

const LinkableTextEditor = ({
  label,
  value,
  links,
  onChangeText,
  onChangeLinks,
  placeholder,
  className,
  hasError = false,
  onModalStateChange,
  controlId,
  minHeight,
}) => {
  const [showLinkModal, setShowLinkModal] = useState(false)
  const [cursorPosition, setCursorPosition] = useState(0)
  const [selectedText, setSelectedText] = useState('')
  const [selectedRange, setSelectedRange] = useState({ start: 0, length: 0 })
  const [editingLinkIndex, setEditingLinkIndex] = useState(null)
  const textareaRef = useRef(null)

  // Define resize helper first so hooks below can reference it
  const autoResize = useCallback(
    (element) => {
      if (!element) return
      element.style.height = 'auto'
      const baseMin = typeof minHeight === 'number' ? Math.max(minHeight, 0) : 0
      const newHeight = Math.max(element.scrollHeight, baseMin)
      element.style.height = newHeight + 'px'
    },
    [minHeight],
  )

  // Update contentEditable div when text or links change
  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current
      const currentText = value || ''
      const rawLinks = Array.isArray(links) ? links : []
      const safeLinks = filterRenderableLinks(rawLinks)

      // Preserve caret when focused
      const isFocused = document.activeElement === el
      const prev = isFocused ? getSelectionOffsets(el) : { start: 0, end: 0 }

      el.dataset.empty = currentText ? 'false' : 'true'

      if (!currentText) {
        if (!isFocused && el.innerHTML !== '' && el.innerHTML !== '<br>') {
          el.innerHTML = ''
        }
        autoResize(el)
        return
      }

      const styledText = renderTextWithLinksUtil(currentText, safeLinks)
      if (el.innerHTML !== styledText) {
        el.innerHTML = styledText
        if (isFocused) {
          setSelectionByCharacterOffsets(el, prev.start, prev.end)
        }
      }

      autoResize(el)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, links])

  // Add CSS to override default link colors in contentEditable
  useEffect(() => {
    const style = document.createElement('style')
    style.textContent = `
      .question-text-editor a { color: var(--gold-deep, #bf9000) !important; }
      .question-text-editor a:visited { color: var(--gold-deep, #bf9000) !important; }
      .question-text-editor a:hover { color: var(--gold-deep, #bf9000) !important; }
      .question-text-editor[data-empty="true"]::before {
        content: attr(data-placeholder);
        color: var(--muted2, #93959b);
        pointer-events: none;
      }
    `
    document.head.appendChild(style)
    return () => {
      document.head.removeChild(style)
    }
  }, [])

  useEffect(() => {
    if (typeof onModalStateChange === 'function') {
      onModalStateChange(showLinkModal)
    }
  }, [showLinkModal, onModalStateChange])

  // Auto-resize on mount
  useEffect(() => {
    if (textareaRef.current) autoResize(textareaRef.current)
  }, [autoResize])

  const getSelectionOffsets = (containerEl) => {
    const sel = window.getSelection()
    if (!sel || sel.rangeCount === 0) return { start: 0, end: 0 }
    const range = sel.getRangeAt(0)
    const preSelectionRange = document.createRange()
    preSelectionRange.selectNodeContents(containerEl)
    preSelectionRange.setEnd(range.startContainer, range.startOffset)
    const start = preSelectionRange.toString().length
    const end = start + range.toString().length
    return { start, end }
  }

  const currentLinksRaw =
    Array.isArray(links) && links.length > 0
      ? links
      : parseLinksFromHtmlUtil(value || '')
  // Filter out zero-length or empty-text links to avoid rendering empty anchors
  const currentLinks = filterRenderableLinks(currentLinksRaw)

  function filterRenderableLinks(incoming) {
    const list = Array.isArray(incoming) ? incoming : []
    return list.filter((l) =>
      typeof l?.startIndex === 'number' && typeof l?.length === 'number'
        ? l.length > 0
        : Boolean(l?.text && l?.text.length > 0),
    )
  }

  function setSelectionByCharacterOffsets(containerEl, start, end) {
    try {
      const range = document.createRange()
      const selection = window.getSelection()
      const s = locateTextNodeAtOffset(containerEl, start)
      const e = locateTextNodeAtOffset(containerEl, end)
      if (!s.node || !e.node) return
      range.setStart(s.node, s.offset)
      range.setEnd(e.node, e.offset)
      selection.removeAllRanges()
      selection.addRange(range)
    } catch (_) {}
  }

  function locateTextNodeAtOffset(containerEl, charOffset) {
    const walker = document.createTreeWalker(
      containerEl,
      NodeFilter.SHOW_TEXT,
      null,
      false,
    )
    let remaining = Math.max(0, charOffset)
    let node = walker.nextNode()
    while (node) {
      const len = node.nodeValue ? node.nodeValue.length : 0
      if (remaining <= len) {
        return { node, offset: remaining }
      }
      remaining -= len
      node = walker.nextNode()
    }
    // Fallback: end of last text node or container
    return {
      node: containerEl.lastChild || containerEl,
      offset:
        containerEl.lastChild &&
        containerEl.lastChild.nodeType === Node.TEXT_NODE
          ? containerEl.lastChild.nodeValue.length
          : containerEl.childNodes.length,
    }
  }

  const canAddLink = selectedText && selectedText.trim()

  return (
    <div className='experience-f position-relative'>
      {label ? (
        <label className='experience-f-label mb-1 d-block' htmlFor={controlId}>
          {label}
        </label>
      ) : null}
      <div className='position-relative'>
        <div
          ref={textareaRef}
          id={controlId}
          contentEditable
          suppressContentEditableWarning={true}
          onClick={(e) => {
            // Check if the clicked element is a link
            const target = e.target
            if (target.tagName === 'A') {
              e.preventDefault()
              e.stopPropagation()

              // Find the link index in the current links array
              const linkText = target.textContent || target.innerText || ''
              const linkUrl = target.getAttribute('data-url') || ''

              // Find the corresponding link in our links array
              const linkIndex = currentLinks.findIndex(
                (link) => link.text === linkText && link.url === linkUrl,
              )

              if (linkIndex !== -1) {
                // Set up the selection to match the clicked link
                const link = currentLinks[linkIndex]
                if (
                  typeof link.startIndex === 'number' &&
                  typeof link.length === 'number'
                ) {
                  setSelectedRange({
                    start: link.startIndex,
                    length: link.length,
                  })
                  setSelectedText(linkText)
                  setEditingLinkIndex(linkIndex)
                  setShowLinkModal(true)
                }
              }
            }
          }}
          onInput={(e) => {
            const newText = e.target.textContent || ''
            const oldText = value || ''
            e.currentTarget.dataset.empty = newText ? 'false' : 'true'
            if (typeof onChangeText === 'function') onChangeText(newText)
            if (
              Array.isArray(currentLinks) &&
              currentLinks.length > 0 &&
              typeof onChangeLinks === 'function'
            ) {
              const updated = adjustLinksOnTextChange(
                oldText,
                newText,
                currentLinks,
              )
              onChangeLinks(filterRenderableLinks(updated))
            }
            autoResize(e.target)
          }}
          onFocus={() => {
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0)
              setCursorPosition(range.startOffset)
              const { start, end } = getSelectionOffsets(textareaRef.current)
              setSelectedRange({ start, length: end - start })
            }
          }}
          onSelect={() => {
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0)
              const start = range.startOffset
              const text = selection.toString()
              setCursorPosition(start)
              setSelectedText(text)
              const offsets = getSelectionOffsets(textareaRef.current)
              setSelectedRange({
                start: offsets.start,
                length: offsets.end - offsets.start,
              })
            }
          }}
          onKeyUp={() => {
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0)
              setCursorPosition(range.startOffset)
              const { start, end } = getSelectionOffsets(textareaRef.current)
              setSelectedRange({ start, length: end - start })
            }
          }}
          onMouseUp={() => {
            const selection = window.getSelection()
            if (selection && selection.rangeCount > 0) {
              const range = selection.getRangeAt(0)
              const start = range.startOffset
              const text = selection.toString()
              setCursorPosition(start)
              setSelectedText(text)
              const offsets = getSelectionOffsets(textareaRef.current)
              setSelectedRange({
                start: offsets.start,
                length: offsets.end - offsets.start,
              })
            }
          }}
          className={`experience-f-control experience-f-textarea question-text-editor ${
            className || ''
          }${hasError ? ' is-invalid' : ''}`}
          style={{
            paddingRight: '2.5rem',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            outline: 'none',
            overflow: 'hidden',
            resize: 'none',
            minHeight:
              typeof minHeight === 'number'
                ? `${Math.max(minHeight, 0)}px`
                : undefined,
          }}
          data-placeholder={placeholder || 'Add the question they see'}
          data-empty={!value ? 'true' : 'false'}
          aria-label={placeholder || label}
        />
        <div
          className='position-absolute'
          style={{
            top: '8px',
            right: '8px',
            zIndex: 10,
            cursor: canAddLink ? 'pointer' : 'not-allowed',
            opacity: canAddLink ? 1 : 0.45,
          }}
          title={
            canAddLink
              ? 'Add link to selected text'
              : 'Select text to add a link'
          }
          onClick={() => {
            if (canAddLink) {
              setEditingLinkIndex(null)
              setShowLinkModal(true)
            }
          }}>
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 24 24'
            style={{
              color: canAddLink
                ? 'var(--gold-deep, #bf9000)'
                : 'var(--muted2, #93959b)',
            }}>
            <path
              d='M10.59 13.41a1.996 1.996 0 0 1 0-2.82l3.88-3.88a2 2 0 1 1 2.83 2.83l-1.17 1.17'
              stroke='currentColor'
              strokeWidth='2'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
            <path
              d='M13.41 10.59a1.996 1.996 0 0 1 0 2.82l-3.88 3.88a2 2 0 1 1-2.83-2.83l1.17-1.17'
              stroke='currentColor'
              strokeWidth='2'
              fill='none'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      <LinkEditorModal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        links={currentLinks}
        editIndex={editingLinkIndex}
        selectedText={selectedText}
        selectedStart={selectedRange?.start || 0}
        onChange={(nextLinks, isUpdatingExistingLink) => {
          const currentText = value || ''
          if (!nextLinks || nextLinks.length === 0) return false
          const { text: linkText, url: linkUrl } = nextLinks[0] || {}
          if (!linkText || !linkUrl) return false

          let newText = currentText
          if (selectedRange && selectedRange.length > 0) {
            const start = Math.max(
              0,
              Math.min(currentText.length, selectedRange.start),
            )
            const end = Math.max(
              start,
              Math.min(currentText.length, start + selectedRange.length),
            )
            newText =
              currentText.slice(0, start) + linkText + currentText.slice(end)
          } else if (selectedText && selectedText.trim()) {
            const idx = currentText.indexOf(
              selectedText,
              cursorPosition - selectedText.length,
            )
            if (idx !== -1)
              newText =
                currentText.slice(0, idx) +
                linkText +
                currentText.slice(idx + selectedText.length)
          } else {
            const beforeCursor = currentText.slice(0, cursorPosition)
            const afterCursor = currentText.slice(cursorPosition)
            newText = beforeCursor + linkText + afterCursor
          }

          if (typeof onChangeText === 'function') onChangeText(newText)

          const baseLinks = Array.isArray(links) ? links : []
          let updatedLinks
          if (isUpdatingExistingLink) {
            updatedLinks = baseLinks.map((link) =>
              link.text === linkText
                ? {
                    text: linkText,
                    url: linkUrl,
                    startIndex: selectedRange.start,
                    length: linkText.length,
                  }
                : link,
            )
          } else {
            const newLink = {
              text: linkText,
              url: linkUrl,
              startIndex: selectedRange.start,
              length: linkText.length,
            }
            updatedLinks = [...baseLinks, newLink]
          }

          if (typeof onChangeLinks === 'function') onChangeLinks(updatedLinks)
          setSelectedText('')
          setSelectedRange({ start: 0, length: 0 })
          return true
        }}
      />
    </div>
  )
}

export default LinkableTextEditor
